import { IconDelete, IconInfo, IconVideo } from '@bynder/icons';
import format from 'format-duration';
import {
  Button,
  Card,
  Inspector,
  List,
  Tooltip,
  notify,
} from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { useActiveWidget } from '../../../../queries/guides';
import {
  CVAsset,
  ModalType,
  VideoWidget,
  WIDGET_TYPE,
} from '../../../../types';
import { AssetType } from '../../../../types/assetTypes';
import { BadVideoNotification } from '../../../../types/notifications';
import {
  VideoSettings,
  useVideoWidgetMutations,
} from '../../../../mutations/widgets/video';
import useVideoDuration from '../../../../hooks/useVideoDuration';
import {
  emptyCSSObject,
  getVideoPreview,
  PREVIEW_EXTENSION,
} from '../../../../constants/widgets';
import { WidgetSettingsHeader } from '../../Styles';
import { useInspectorStore } from '../../../../stores/inspectorStore';
import { useModalStore } from '../../../../stores/modalStore';
import MediaBorderRadius from '../MediaBorderRadius';
import CustomCssStyleEditor from '../CustomCSSEditor';
import { isDevelopment } from '../../../../constants/app';

export const VideoWidgetSettings = () => {
  const videoWidget = useActiveWidget() as VideoWidget;
  const openModal = useModalStore.use.openModal();
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const { updateVideoWidget, editStyle } = useVideoWidgetMutations(
    widgetSettings?.sectionId,
    widgetSettings?.columnIndex,
    widgetSettings?.widgetId,
  );

  const addVideoAsset = (selectedMedia: CVAsset[]) => {
    if (selectedMedia.length) {
      const { databaseId, name, url, previewUrls, derivatives, originalUrl } =
        selectedMedia[0];
      // MP4 is the only format compatible with all the browsers
      const mp4Preview = getVideoPreview(
        previewUrls,
        PREVIEW_EXTENSION,
        originalUrl,
      );

      if (!mp4Preview) {
        notify(BadVideoNotification);

        return;
      }

      updateVideoWidget.mutate({
        assetId: databaseId,
        assetName: name,
        detailUrl: url,
        videoUrl: mp4Preview,
        coverUrl: derivatives.webImage,
      });
    }
  };

  const modalProps = {
    compactViewProps: {
      selectedAssets: [videoWidget?.assetId],
      assetTypes: [AssetType.VIDEO],
    },
  };

  const handleClick = () => {
    openModal({
      modalType: ModalType.COMPACT_VIEW_MODAL,
      modalOnSubmit: addVideoAsset,
      modalProps,
    });
  };
  const handleEditStyle = (style: string) => {
    editStyle.mutate({ style });
  };
  return (
    <>
      <WidgetSettingsHeader widgetType={WIDGET_TYPE.VIDEO} />
      <List>
        {videoWidget?.videoUrl ? (
          <>
            <VideoWidgetCard handleClick={handleClick} />
            <VideoWidgetToggles />
            <Inspector.Divider />
            <MediaBorderRadius
              style={
                videoWidget?.style?.stylesheet ?? emptyCSSObject.stylesheet
              }
              onEditStyle={handleEditStyle}
            />

            {isDevelopment && (
              <>
                <Inspector.Divider />
                <CustomCssStyleEditor
                  handleChange={() => editStyle.mutate({ style: '' })}
                  label="Section CSS"
                  placeholder="Enter your custom CSS here"
                  style={videoWidget?.style?.stylesheet}
                />
              </>
            )}
          </>
        ) : (
          <EmptyWidgetButton handleClick={handleClick} />
        )}
      </List>
    </>
  );
};

const VideoWidgetToggles = () => {
  const videoWidget = useActiveWidget() as VideoWidget;
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const { translate } = useTranslate();
  const { toggleVideoWidgetSettings } = useVideoWidgetMutations(
    widgetSettings?.sectionId,
    widgetSettings?.columnIndex,
    widgetSettings?.widgetId,
  );

  const handleControlsToggle = () => {
    toggleVideoWidgetSettings.mutate({
      [VideoSettings.controls]: !videoWidget.controls,
    });
  };

  const handleAutoplayToggle = () => {
    toggleVideoWidgetSettings.mutate({
      [VideoSettings.autoplay]: !videoWidget.autoplay,
    });
  };
  const handleLoopToggle = () => {
    toggleVideoWidgetSettings.mutate({
      [VideoSettings.loop]: !videoWidget.loop,
    });
  };
  return (
    <>
      <List.Item
        checkedVariant="switch"
        isChecked={videoWidget.controls}
        onClick={handleControlsToggle}
      >
        {translate('WIDGET_INSPECTOR.VIDEO_CONTROLS_TOGGLE')}
      </List.Item>
      <List.Item
        checkedVariant="switch"
        isChecked={videoWidget.autoplay}
        onClick={handleAutoplayToggle}
      >
        <Tooltip
          content={translate('WIDGET_INSPECTOR.VIDEO_AUTPLAY_MUTED_TOOLTIP')}
          timing="delay"
        >
          {translate('WIDGET_INSPECTOR.VIDEO_AUTOPLAY_TOGGLE')} <IconInfo />
        </Tooltip>
      </List.Item>
      <List.Item
        checkedVariant="switch"
        isChecked={videoWidget.loop}
        onClick={handleLoopToggle}
      >
        {translate('WIDGET_INSPECTOR.VIDEO_LOOP_TOGGLE')}
      </List.Item>
    </>
  );
};

const EmptyWidgetButton = ({ handleClick }) => {
  const { translate } = useTranslate();
  return (
    <Button
      onClick={handleClick}
      isFullWidth
      variant="secondary"
      icon={<IconVideo />}
    >
      {translate('WIDGET_INSPECTOR.EMPTY_VIDEO_WIDGET_BUTTON')}
    </Button>
  );
};

const VideoWidgetCard = ({ handleClick }) => {
  const { translate } = useTranslate();
  const videoWidget = useActiveWidget() as VideoWidget;
  const videoDuration = useVideoDuration(videoWidget?.videoUrl);
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const { updateVideoWidget } = useVideoWidgetMutations(
    widgetSettings?.sectionId,
    widgetSettings?.columnIndex,
    widgetSettings?.widgetId,
  );

  const clearVideo = () => {
    updateVideoWidget.mutate({
      assetId: '',
      assetName: '',
      detailUrl: '',
      videoUrl: '',
      coverUrl: '',
    });
  };

  return (
    <Card
      variant="clean"
      onClick={handleClick}
      contextAction={
        <Card.ContextActionButton
          tooltip={translate('WIDGET_INSPECTOR.CLEAR_VIDEO_BUTTON')}
          aria-label="Clear video"
          onClick={clearVideo}
        >
          <IconDelete />
        </Card.ContextActionButton>
      }
    >
      <Card.Frame>
        {videoWidget?.coverUrl ? (
          <Card.Images>
            <Card.Img contentResize="fit" src={videoWidget?.coverUrl} />
          </Card.Images>
        ) : (
          <Card.IconPlaceholder>
            <IconVideo aria-label="coverIcon" />
          </Card.IconPlaceholder>
        )}
      </Card.Frame>
      {videoWidget?.videoUrl && (
        <Card.Header
          title={videoWidget.assetName}
          detail={format(videoDuration * 1000)}
        />
      )}
    </Card>
  );
};
